import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const CategoryBreakdownChart = ({ interval, apiBaseUrl }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Color palette for categories
  const COLORS = [
    '#667eea', // Purple
    '#f093fb', // Pink
    '#4facfe', // Blue
    '#43e97b', // Green
    '#fa709a', // Rose
    '#feca57', // Yellow
    '#48dbfb', // Cyan
    '#ff6348', // Orange
  ];

  useEffect(() => {
    fetchCategoryBreakdown();
  }, [interval]);

  const fetchCategoryBreakdown = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${apiBaseUrl}/chat_safety/alert_category_breakdown/${interval}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch category breakdown data');
      }

      const result = await response.json();
      
      // Transform and sort the data
      const transformedData = result
        .map((item, index) => ({
          category: item[0] || 'Unknown',
          occurrences: item[1] || 0,
          color: COLORS[index % COLORS.length],
        }))
        .sort((a, b) => b.occurrences - a.occurrences); // Sort by occurrences descending

      setData(transformedData);
    } catch (err) {
      console.error('Error fetching category breakdown:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const percentage = data.length > 0 
        ? ((payload[0].value / data.reduce((sum, item) => sum + item.occurrences, 0)) * 100).toFixed(1)
        : 0;
      
      return (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}>
          <p style={{
            margin: '0 0 6px 0',
            fontWeight: '600',
            color: '#2d3748',
            fontSize: '13px',
          }}>
            {payload[0].payload.category}
          </p>
          <p style={{
            margin: '0',
            fontSize: '12px',
            color: '#4a5568',
          }}>
            Occurrences: <strong style={{ color: '#1a202c' }}>{payload[0].value}</strong>
          </p>
          <p style={{
            margin: '4px 0 0 0',
            fontSize: '12px',
            color: '#4a5568',
          }}>
            Percentage: <strong style={{ color: '#1a202c' }}>{percentage}%</strong>
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom label for bars
  const renderCustomLabel = (props) => {
    const { x, y, width, height, value } = props;
    const radius = 10;
    
    return (
      <text
        x={x + width + 5}
        y={y + height / 2}
        fill="#4a5568"
        textAnchor="start"
        dominantBaseline="middle"
        style={{ fontSize: '12px', fontWeight: '600' }}
      >
        {value}
      </text>
    );
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        color: '#718096',
        fontSize: '14px',
        gap: '12px',
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid #e2e8f0',
          borderTopColor: '#667eea',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}></div>
        <span>Loading category breakdown...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        color: '#e53e3e',
        fontSize: '14px',
      }}>
        <p>Error loading breakdown: {error}</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        color: '#718096',
        fontSize: '14px',
      }}>
        <p>No category data available for this period</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', minHeight: '300px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 10, right: 60, left: 10, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
          <XAxis
            type="number"
            stroke="#718096"
            style={{ fontSize: '12px' }}
            tickLine={false}
            allowDecimals={false}
          />
          <YAxis
            type="category"
            dataKey="category"
            stroke="#718096"
            style={{ fontSize: '12px' }}
            tickLine={false}
            width={150}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(102, 126, 234, 0.05)' }} />
          <Bar
            dataKey="occurrences"
            radius={[0, 8, 8, 0]}
            label={renderCustomLabel}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CategoryBreakdownChart;